package dev.lingvo.site.pages.app.projects.slug

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import dev.lingvo.shared.ProposalDto
import dev.lingvo.shared.validateSourceSnapshot
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.i18n.t
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/** Historical source is independent of both today's scalar source and the suggested target. */
@Composable
internal fun ProposalsSource(proposal: ProposalDto) {
    val snapshot = proposal.sourceSnapshot?.takeIf {
        it.view.keyId == proposal.keyId && validateSourceSnapshot(it) == null
    }
    Column(Modifier.fillMaxWidth().gap(Space.XXS)) {
        LMuted(t("app.projects.slug.proposals_source.captured_source", "Captured source"))
        if (snapshot == null) {
            Span(Modifier.toAttrs { attr("data-proposals-source-unavailable", "true") }) {
                Text(t("app.projects.slug.proposals_source.captured_source_unavailable", "Captured source unavailable"))
            }
        } else {
            Column(Modifier.fillMaxWidth().gap(Space.XXS)
                .attr("data-proposals-source", "captured").attr("lang", snapshot.view.sourceLocale)) {
                if (snapshot.view.isPlural) {
                    val forms = snapshot.pluralForms.orEmpty()
                    if (forms.isEmpty()) ProposalsEmptySource()
                    forms.entries.sortedBy { it.key.ordinal }.forEach { (category, value) ->
                        Column(Modifier.fillMaxWidth().gap(Space.XXS)
                            .attr("data-proposals-source-form", category.name.lowercase())) {
                            LMuted(category.name.lowercase())
                            ProposalsSourceText(value)
                        }
                    }
                } else {
                    val value = snapshot.value
                    if (value.isNullOrEmpty()) ProposalsEmptySource()
                    if (value != null) ProposalsSourceText(value)
                }
            }
        }
    }
    proposal.sourceValue?.takeIf { snapshot == null || snapshot.view.isPlural || it != snapshot.value }?.let { current ->
        Column(Modifier.fillMaxWidth().gap(Space.XXS)) {
            LMuted(t("app.projects.slug.proposals_source.current_source_single_form", "Current source (single form)"))
            Span(Modifier.fontSize(TypeScale.BodySmall).styleModifier { property("white-space", "pre-wrap") }.toAttrs()) {
                Text(current)
            }
        }
    }
}

@Composable
private fun ProposalsSourceText(value: String) {
    Span(Modifier.fontSize(TypeScale.BodySmall).styleModifier {
        property("white-space", "pre-wrap")
        property("overflow-wrap", "anywhere")
    }.toAttrs { attr("data-proposals-source-text", "true"); attr("dir", "auto") }) {
        Text(value)
    }
    if (value.isEmpty()) LMuted(t("app.projects.slug.proposals_source.empty_source_form", "Empty source form"))
}

@Composable
private fun ProposalsEmptySource() {
    Span(Modifier.toAttrs { attr("data-proposals-source-empty", "true") }) {
        Text(t("app.projects.slug.proposals_source.captured_source_is_empty", "Captured source is empty"))
    }
}
